/**
 * A compact QR Code encoder (ISO/IEC 18004): byte mode, error correction level M, versions 1–15
 * (up to 412 bytes — an otpauth:// URL needs ~120). No dependencies; follows the reference
 * algorithm (Reed–Solomon over GF(256), interleaved blocks, all 8 masks scored by the standard
 * penalty rules). Returns the module matrix: `true` = dark.
 */

/** Per version (index = version): [ecPerBlock, blocks1, data1, blocks2, data2] for level M. */
const BLOCKS_M: readonly (readonly [number, number, number, number, number])[] = [
  [0, 0, 0, 0, 0],
  [10, 1, 16, 0, 0],
  [16, 1, 28, 0, 0],
  [26, 1, 44, 0, 0],
  [18, 2, 32, 0, 0],
  [24, 2, 43, 0, 0],
  [16, 4, 27, 0, 0],
  [18, 4, 31, 0, 0],
  [22, 2, 38, 2, 39],
  [22, 3, 36, 2, 37],
  [26, 4, 43, 1, 44],
  [30, 1, 50, 4, 51],
  [22, 6, 36, 2, 37],
  [22, 8, 37, 1, 38],
  [24, 4, 40, 5, 41],
  [24, 5, 41, 5, 42],
];

const ALIGNMENT: readonly (readonly number[])[] = [
  [],
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
  [6, 30, 54],
  [6, 32, 58],
  [6, 34, 62],
  [6, 26, 46, 66],
  [6, 26, 48, 70],
];

const MAX_VERSION = 15;

function blockSpec(version: number) {
  const spec = BLOCKS_M[version];
  if (!spec) throw new Error(`Unsupported QR version ${String(version)}`);
  const [ec, b1, d1, b2, d2] = spec;
  return { ec, b1, d1, b2, d2, dataCodewords: b1 * d1 + b2 * d2 };
}

// --- Reed–Solomon over GF(256), polynomial 0x11D ---------------------------------------

function gfMul(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
}

function rsDivisor(degree: number): number[] {
  const result = new Array<number>(degree).fill(0);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < degree; j++) {
      result[j] = gfMul(result[j] ?? 0, root);
      if (j + 1 < degree) result[j] = (result[j] ?? 0) ^ (result[j + 1] ?? 0);
    }
    root = gfMul(root, 0x02);
  }
  return result;
}

function rsRemainder(data: readonly number[], divisor: readonly number[]): number[] {
  const result = new Array<number>(divisor.length).fill(0);
  for (const b of data) {
    const factor = b ^ (result.shift() ?? 0);
    result.push(0);
    for (let i = 0; i < divisor.length; i++)
      result[i] = (result[i] ?? 0) ^ gfMul(divisor[i] ?? 0, factor);
  }
  return result;
}

// --- data codewords ------------------------------------------------------------------

function dataCodewords(bytes: Uint8Array, version: number): number[] {
  const { dataCodewords: capacity } = blockSpec(version);
  const bits: number[] = [];
  const put = (value: number, length: number) => {
    for (let i = length - 1; i >= 0; i--) bits.push((value >>> i) & 1);
  };
  put(0b0100, 4); // byte mode
  put(bytes.length, version <= 9 ? 8 : 16);
  for (const b of bytes) put(b, 8);
  const capacityBits = capacity * 8;
  put(0, Math.min(4, capacityBits - bits.length)); // terminator
  while (bits.length % 8 !== 0) bits.push(0);
  const out: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let v = 0;
    for (let j = 0; j < 8; j++) v = (v << 1) | (bits[i + j] ?? 0);
    out.push(v);
  }
  for (let pad = 0xec; out.length < capacity; pad ^= 0xec ^ 0x11) out.push(pad);
  return out;
}

function interleave(data: readonly number[], version: number): number[] {
  const { ec, b1, d1, b2, d2 } = blockSpec(version);
  const divisor = rsDivisor(ec);
  const blocks: { data: number[]; ec: number[] }[] = [];
  let k = 0;
  for (let i = 0; i < b1 + b2; i++) {
    const len = i < b1 ? d1 : d2;
    const chunk = data.slice(k, k + len);
    k += len;
    blocks.push({ data: chunk, ec: rsRemainder(chunk, divisor) });
  }
  const out: number[] = [];
  const maxData = Math.max(d1, d2);
  for (let i = 0; i < maxData; i++)
    for (const b of blocks) if (i < b.data.length) out.push(b.data[i] ?? 0);
  for (let i = 0; i < ec; i++) for (const b of blocks) out.push(b.ec[i] ?? 0);
  return out;
}

// --- matrix --------------------------------------------------------------------------

class Matrix {
  readonly version: number;
  readonly size: number;
  readonly dark: boolean[][];
  readonly fixed: boolean[][];

  constructor(version: number) {
    this.version = version;
    this.size = version * 4 + 17;
    this.dark = Array.from({ length: this.size }, () => new Array<boolean>(this.size).fill(false));
    this.fixed = Array.from({ length: this.size }, () => new Array<boolean>(this.size).fill(false));
  }

  set(x: number, y: number, dark: boolean) {
    const row = this.dark[y];
    const fixedRow = this.fixed[y];
    if (!row || !fixedRow || x < 0 || x >= this.size) return;
    row[x] = dark;
    fixedRow[x] = true;
  }

  drawFunctionPatterns() {
    const n = this.size;
    for (let i = 0; i < n; i++) {
      this.set(6, i, i % 2 === 0);
      this.set(i, 6, i % 2 === 0);
    }
    for (const [cx, cy] of [
      [3, 3],
      [n - 4, 3],
      [3, n - 4],
    ] as const) {
      for (let dy = -4; dy <= 4; dy++) {
        for (let dx = -4; dx <= 4; dx++) {
          const dist = Math.max(Math.abs(dx), Math.abs(dy));
          const x = cx + dx;
          const y = cy + dy;
          if (x >= 0 && x < n && y >= 0 && y < n) this.set(x, y, dist !== 2 && dist !== 4);
        }
      }
    }
    const pos = ALIGNMENT[this.version] ?? [];
    const last = pos.length - 1;
    pos.forEach((ax, i) =>
      pos.forEach((ay, j) => {
        if ((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0)) return;
        for (let dy = -2; dy <= 2; dy++)
          for (let dx = -2; dx <= 2; dx++)
            this.set(ax + dx, ay + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
      }),
    );
    this.drawFormat(0); // reserve the format areas (redrawn with the real mask later)
    this.drawVersion();
  }

  drawFormat(mask: number) {
    const n = this.size;
    const data = (0b00 << 3) | mask; // level M = 00
    let rem = data;
    for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    const bits = ((data << 10) | rem) ^ 0x5412;
    const bit = (i: number) => ((bits >>> i) & 1) !== 0;
    for (let i = 0; i <= 5; i++) this.set(8, i, bit(i));
    this.set(8, 7, bit(6));
    this.set(8, 8, bit(7));
    this.set(7, 8, bit(8));
    for (let i = 9; i < 15; i++) this.set(14 - i, 8, bit(i));
    for (let i = 0; i < 8; i++) this.set(n - 1 - i, 8, bit(i));
    for (let i = 8; i < 15; i++) this.set(8, n - 15 + i, bit(i));
    this.set(8, n - 8, true); // the dark module
  }

  drawVersion() {
    if (this.version < 7) return;
    let rem = this.version;
    for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    const bits = (this.version << 12) | rem;
    for (let i = 0; i < 18; i++) {
      const dark = ((bits >>> i) & 1) !== 0;
      const a = this.size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      this.set(a, b, dark);
      this.set(b, a, dark);
    }
  }

  drawCodewords(codewords: readonly number[]) {
    const n = this.size;
    let i = 0;
    for (let right = n - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5;
      for (let vert = 0; vert < n; vert++) {
        for (let j = 0; j < 2; j++) {
          const x = right - j;
          const upward = ((right + 1) & 2) === 0;
          const y = upward ? n - 1 - vert : vert;
          const row = this.dark[y];
          if (!row || this.fixed[y]?.[x]) continue;
          if (i < codewords.length * 8) {
            row[x] = (((codewords[i >>> 3] ?? 0) >>> (7 - (i & 7))) & 1) !== 0;
            i++;
          }
        }
      }
    }
  }

  applyMask(mask: number) {
    for (let y = 0; y < this.size; y++) {
      const row = this.dark[y];
      if (!row) continue;
      for (let x = 0; x < this.size; x++) {
        if (this.fixed[y]?.[x]) continue;
        let invert: boolean;
        switch (mask) {
          case 0:
            invert = (x + y) % 2 === 0;
            break;
          case 1:
            invert = y % 2 === 0;
            break;
          case 2:
            invert = x % 3 === 0;
            break;
          case 3:
            invert = (x + y) % 3 === 0;
            break;
          case 4:
            invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0;
            break;
          case 5:
            invert = ((x * y) % 2) + ((x * y) % 3) === 0;
            break;
          case 6:
            invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
            break;
          default:
            invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
        }
        if (invert) row[x] = !row[x];
      }
    }
  }

  /** Standard penalty score (rules 1–4); lower is better. */
  penalty(): number {
    const n = this.size;
    const at = (x: number, y: number) => this.dark[y]?.[x] === true;
    let score = 0;
    // Rule 1: runs of 5+ same-colour modules in rows and columns.
    for (let pass = 0; pass < 2; pass++) {
      for (let a = 0; a < n; a++) {
        let run = 1;
        for (let b = 1; b < n; b++) {
          const same = pass === 0 ? at(b, a) === at(b - 1, a) : at(a, b) === at(a, b - 1);
          if (same) run++;
          else {
            if (run >= 5) score += run - 2;
            run = 1;
          }
        }
        if (run >= 5) score += run - 2;
      }
    }
    // Rule 2: 2×2 blocks of one colour.
    for (let y = 0; y < n - 1; y++)
      for (let x = 0; x < n - 1; x++) {
        const c = at(x, y);
        if (c === at(x + 1, y) && c === at(x, y + 1) && c === at(x + 1, y + 1)) score += 3;
      }
    // Rule 3: finder-like 1:1:3:1:1 patterns with 4 light modules on one side.
    const pattern = [true, false, true, true, true, false, true];
    for (let a = 0; a < n; a++) {
      for (let b = 0; b + 7 <= n; b++) {
        for (let pass = 0; pass < 2; pass++) {
          const get = (k: number) => (pass === 0 ? at(b + k, a) : at(a, b + k));
          if (!pattern.every((p, k) => get(k) === p)) continue;
          const lightBefore = [1, 2, 3, 4].every((k) => b - k < 0 || !get(-k));
          const lightAfter = [7, 8, 9, 10].every((k) => b + k >= n || !get(k));
          if (lightBefore || lightAfter) score += 40;
        }
      }
    }
    // Rule 4: balance of dark modules.
    let darkCount = 0;
    for (const row of this.dark) for (const v of row) if (v) darkCount++;
    const total = n * n;
    score += Math.floor(Math.abs(darkCount * 20 - total * 10) / total) * 10;
    return score;
  }
}

/** Encodes `text` (UTF-8) as a QR code matrix. Throws if it does not fit version 15-M. */
export function encodeQr(text: string): boolean[][] {
  const bytes = new TextEncoder().encode(text);
  let version = 1;
  for (; version <= MAX_VERSION; version++) {
    const headerBits = 4 + (version <= 9 ? 8 : 16);
    if (headerBits + bytes.length * 8 <= blockSpec(version).dataCodewords * 8) break;
  }
  if (version > MAX_VERSION) throw new Error('Text too long for a QR code');
  const codewords = interleave(dataCodewords(bytes, version), version);

  let best: Matrix | null = null;
  let bestScore = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    const m = new Matrix(version);
    m.drawFunctionPatterns();
    m.drawCodewords(codewords);
    m.applyMask(mask);
    m.drawFormat(mask);
    const score = m.penalty();
    if (score < bestScore) {
      best = m;
      bestScore = score;
    }
  }
  return (best ?? new Matrix(version)).dark;
}

/** One SVG path (`M x y h1 v1 h-1 z` per dark module) for a matrix, with a quiet zone of `margin`. */
export function qrPath(matrix: readonly (readonly boolean[])[], margin = 4): string {
  const parts: string[] = [];
  matrix.forEach((row, y) =>
    row.forEach((dark, x) => {
      if (dark) parts.push(`M${String(x + margin)} ${String(y + margin)}h1v1h-1z`);
    }),
  );
  return parts.join('');
}
